import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, MenuItem, Modal, Paper, TextField, Typography} from "@mui/material";
import {useAlert} from "../context/AlertContext";
import {LessonType, ScheduleSlot} from "../types";

type Mode = "create" | "edit";

interface Props {
    open: boolean;
    mode: Mode;
    slot: ScheduleSlot | null;
    defaultDayOfWeek: number;
    defaultStartTime: string;
    defaultEndTime: string;
    defaultActiveFrom: string;
    defaultActiveTo: string;
    onClose: () => void;
    onSubmit: (payload: {
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        activeFrom: string;
        activeTo: string;
        lessonType: LessonType;
        notes?: string | null;
    }) => Promise<void>;
    submitting: boolean;
    onDelete?: () => void;
    allowDelete?: boolean;
}

const lessonTypes: LessonType[] = ["LECTURE", "PRACTICE", "OTHER"];

const dayOptions = [
    {value: 1, labelKey: "scheduleslotmodal.days.monday"},
    {value: 2, labelKey: "scheduleslotmodal.days.tuesday"},
    {value: 3, labelKey: "scheduleslotmodal.days.wednesday"},
    {value: 4, labelKey: "scheduleslotmodal.days.thursday"},
    {value: 5, labelKey: "scheduleslotmodal.days.friday"},
    {value: 6, labelKey: "scheduleslotmodal.days.saturday"},
    {value: 7, labelKey: "scheduleslotmodal.days.sunday"},
];

const lessonTypeLabelKey: Record<LessonType, string> = {
    LECTURE: "scheduleslotmodal.lessonTypes.lecture",
    PRACTICE: "scheduleslotmodal.lessonTypes.practice",
    OTHER: "scheduleslotmodal.lessonTypes.other",
};

const padTime = (t: string) => (t?.length === 5 ? t : t?.slice(0, 5));

const ScheduleSlotModal = ({
                               open,
                               mode,
                               slot,
                               defaultDayOfWeek,
                               defaultStartTime,
                               defaultEndTime,
                               defaultActiveFrom,
                               defaultActiveTo,
                               onClose,
                               onSubmit,
                               submitting,
                               onDelete,
                               allowDelete = true,
                           }: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const initial = useMemo(() => {
        if (mode === "edit" && slot) {
            return {
                dayOfWeek: slot.dayOfWeek,
                startTime: padTime(slot.startTime),
                endTime: padTime(slot.endTime),
                activeFrom: slot.activeFrom,
                activeTo: slot.activeTo,
                lessonType: slot.lessonType,
                notes: slot.notes ?? "",
            };
        }

        return {
            dayOfWeek: defaultDayOfWeek,
            startTime: defaultStartTime,
            endTime: defaultEndTime,
            activeFrom: defaultActiveFrom,
            activeTo: defaultActiveTo,
            lessonType: "LECTURE" as LessonType,
            notes: "",
        };
    }, [mode, slot, defaultDayOfWeek, defaultStartTime, defaultEndTime, defaultActiveFrom, defaultActiveTo]);

    const [dayOfWeek, setDayOfWeek] = useState(initial.dayOfWeek);
    const [startTime, setStartTime] = useState(initial.startTime);
    const [endTime, setEndTime] = useState(initial.endTime);
    const [activeFrom, setActiveFrom] = useState(initial.activeFrom);
    const [activeTo, setActiveTo] = useState(initial.activeTo);
    const [lessonType, setLessonType] = useState<LessonType>(initial.lessonType);
    const [notes, setNotes] = useState(initial.notes);

    useEffect(() => {
        if (!open) return;

        setDayOfWeek(initial.dayOfWeek);
        setStartTime(initial.startTime);
        setEndTime(initial.endTime);
        setActiveFrom(initial.activeFrom);
        setActiveTo(initial.activeTo);
        setLessonType(initial.lessonType);
        setNotes(initial.notes);
    }, [open, initial]);

    const handleSubmit = async () => {
        if (!startTime || !endTime) {
            showAlert(t("scheduleslotmodal.validation.timeRequired"), "error");
            return;
        }

        if (startTime >= endTime) {
            showAlert(t("scheduleslotmodal.validation.startBeforeEnd"), "error");
            return;
        }

        if (!activeFrom || !activeTo) {
            showAlert(t("scheduleslotmodal.validation.activeDatesRequired"), "error");
            return;
        }

        if (activeFrom > activeTo) {
            showAlert(t("scheduleslotmodal.validation.activeFromBeforeTo"), "error");
            return;
        }

        await onSubmit({
            dayOfWeek,
            startTime,
            endTime,
            activeFrom,
            activeTo,
            lessonType,
            notes: notes?.trim() ? notes.trim() : null,
        });
    };

    const title = mode === "edit"
        ? t("scheduleslotmodal.editSlot")
        : t("scheduleslotmodal.addSlot");

    return (
        <Modal open={open} onClose={submitting ? undefined : onClose}>
            <Box sx={{display: "flex", alignItems: "flex-start", justifyContent: "center", minHeight: "100vh"}}>
                <Paper
                    sx={{
                        width: 520,
                        borderRadius: 2,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                    elevation={0}
                >
                    <Box sx={{p: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}`}}>
                        <Typography sx={{fontWeight: 800}}>
                            {title}
                        </Typography>
                    </Box>

                    <Box sx={{p: 2, display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField
                            select
                            label={t("scheduleslotmodal.dayOfWeek")}
                            value={dayOfWeek}
                            onChange={(e) => setDayOfWeek(Number(e.target.value))}
                            size="small"
                        >
                            {dayOptions.map((day) => (
                                <MenuItem key={day.value} value={day.value}>
                                    {t(day.labelKey)}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{display: "flex", gap: 2}}>
                            <TextField
                                label={t("scheduleslotmodal.startTime")}
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{inputLabel: {shrink: true}}}
                            />

                            <TextField
                                label={t("scheduleslotmodal.endTime")}
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        </Box>

                        <Box sx={{display: "flex", gap: 2}}>
                            <TextField
                                label={t("scheduleslotmodal.activeFrom")}
                                type="date"
                                value={activeFrom}
                                onChange={(e) => setActiveFrom(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{inputLabel: {shrink: true}}}
                            />

                            <TextField
                                label={t("scheduleslotmodal.activeTo")}
                                type="date"
                                value={activeTo}
                                onChange={(e) => setActiveTo(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        </Box>

                        <TextField
                            select
                            label={t("scheduleslotmodal.lessonType")}
                            value={lessonType}
                            onChange={(e) => setLessonType(e.target.value as LessonType)}
                            size="small"
                        >
                            {lessonTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {t(lessonTypeLabelKey[type])}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label={t("scheduleslotmodal.notes")}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            size="small"
                            multiline
                            minRows={3}
                            placeholder={t("scheduleslotmodal.notesPlaceholder")}
                        />
                    </Box>

                    <Box
                        sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box>
                            {mode === "edit" && slot && allowDelete ? (
                                <Button
                                    color="error"
                                    variant="outlined"
                                    onClick={onDelete}
                                    disabled={submitting}
                                    sx={{textTransform: "none"}}
                                >
                                    {t("common.delete")}
                                </Button>
                            ) : null}
                        </Box>

                        <Box sx={{display: "flex", gap: 1}}>
                            <Button onClick={onClose} disabled={submitting}>
                                {t("common.cancel")}
                            </Button>

                            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                                {t("common.save")}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default ScheduleSlotModal;